import { useEffect, useState, Suspense, lazy } from 'react';
import { Route } from 'react-router-dom';
import { BrowserRouter as Router, Routes } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { Container, Spinner } from 'react-bootstrap';
import './App.scss';
import { NotificationContainer } from 'react-notifications';
import { GrabData } from 'components/GrabData';
import { StatusBar } from 'components/StatusBar';
import { getPublicMyWorkflowUrl } from 'helper/utils';
// route components are lazy-loaded below

function RedirectToPublicStudio() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const templateId = params.get('template');
    window.location.replace(getPublicMyWorkflowUrl(templateId || undefined));
  }, []);

  return (
    <div className='route-loading'>
      <Spinner animation='border' variant='light' size='sm' /> Redirecting to Studio…
    </div>
  );
}

// Convert route components to lazy-loaded modules for code splitting
const MediaList = lazy(() =>
  import('./containers/MediaList').then(m => ({ default: m.MediaList }))
);
const MediaDetail = lazy(() =>
  import('containers/MediaDetail').then(m => ({ default: m.MediaDetail }))
);
const UsersList = lazy(() =>
  import('containers/UsersList').then(m => ({ default: m.UsersList }))
);
const UserDetailPage = lazy(() =>
  import('containers/UserDetailPage').then(m => ({ default: m.UserDetailPage }))
);
const BatchList = lazy(() =>
  import('containers/BatchList').then(m => ({ default: m.BatchList }))
);
const BatchDetail = lazy(() =>
  import('containers/BatchDetail').then(m => ({ default: m.BatchDetail }))
);
const CustomersList = lazy(() =>
  import('containers/CustomersList/CustomersList').then(m => ({
    default: m.CustomersList
  }))
);
const CustomerDetailPage = lazy(() =>
  import('containers/CustomerDetailPage/CustomerDetailPage').then(m => ({
    default: m.CustomerDetailPage
  }))
);
const DashboardPage = lazy(() =>
  import('containers/DashboardPage/DashboardPage').then(m => ({
    default: m.DashboardPage
  }))
);
const AdminSidebar = lazy(() =>
  import('components/AdminSidebar/AdminSidebar').then(m => ({
    default: m.AdminSidebar
  }))
);
const UserGroupsList = lazy(() =>
  import('containers/UserGroupsList').then(m => ({ default: m.UserGroupsList }))
);
const UserGroupUsers = lazy(() =>
  import('containers/UserGroupUsers').then(m => ({ default: m.UserGroupUsers }))
);
const UserGroupDetail = lazy(() =>
  import('containers/UserGroupDetail').then(m => ({
    default: m.UserGroupDetail
  }))
);
const SettingsPage = lazy(() =>
  import('containers/SettingsPage').then(m => ({ default: m.SettingsPage }))
);
const SettingDetailPage = lazy(() =>
  import('containers/SettingDetailPage/SettingDetailPage').then(m => ({
    default: m.SettingDetailPage
  }))
);
const AssetsList = lazy(() =>
  import('containers/AssetsList').then(m => ({ default: m.AssetsList }))
);
const StatisticsPage = lazy(() =>
  import('containers/StatisticsPage').then(m => ({ default: m.StatisticsPage }))
);
const AssetDetail = lazy(() =>
  import('containers/AssetDetail').then(m => ({ default: m.AssetDetail }))
);
const CouponsList = lazy(() =>
  import('containers/CouponsList').then(m => ({ default: m.CouponsList }))
);
const CouponDetail = lazy(() =>
  import('containers/CouponDetail').then(m => ({ default: m.CouponDetail }))
);
const WorkflowList = lazy(() =>
  import('containers/WorkflowList/WorkflowList').then(m => ({
    default: m.WorkflowList
  }))
);
const WorkflowDetail = lazy(() =>
  import('containers/WorkflowDetail').then(m => ({ default: m.WorkflowDetail }))
);
const WorkflowTemplatesAdmin = lazy(() =>
  import('containers/WorkflowTemplatesAdmin').then(m => ({
    default: m.WorkflowTemplatesAdmin
  }))
);
const WorkflowTemplateDetail = lazy(() =>
  import('containers/WorkflowTemplateDetail').then(m => ({
    default: m.WorkflowTemplateDetail
  }))
);
const EventsList = lazy(() =>
  import('containers/EventsList').then(m => ({ default: m.EventsList }))
);
const EventDetail = lazy(() =>
  import('containers/EventDetail').then(m => ({ default: m.EventDetail }))
);
const SessionsList = lazy(() =>
  import('containers/SessionsList').then(m => ({ default: m.SessionsList }))
);
const SessionDetail = lazy(() =>
  import('containers/SessionDetail').then(m => ({ default: m.SessionDetail }))
);

const AdminShell = () => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Container fluid className='main-container'>
      <div className='layout-wrapper'>
        <Suspense
          fallback={
            <div className='route-loading'>
              <Spinner animation='border' variant='light' size='sm' /> Loading...
            </div>
          }
        >
          <AdminSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        </Suspense>
        <div
          className={`page-content-wrapper ${collapsed ? 'collapsed' : ''}`}
        >
          <div className='page-content-body'>
            <StatusBar />
            <GrabData />
            <Suspense
              fallback={
                <div className='route-loading'>
                  <Spinner animation='border' variant='primary' size='sm' />{' '}
                  Loading...
                </div>
              }
            >
              <Routes>
                <Route exact path='/' element={<DashboardPage />} />
                <Route exact path='/media' element={<MediaList />} />
                <Route
                  exact
                  path='/media/:media_id'
                  element={<MediaDetail />}
                />
                <Route exact path='/batch' element={<BatchList />} />
                <Route
                  exact
                  path='/batch/:batch_id'
                  element={<BatchDetail />}
                />

                {/* USER GROUPS ROUTING */}
                <Route path='/user-groups' element={<UserGroupsList />} />
                <Route
                  path='/user-groups/:group_name'
                  element={<UserGroupDetail />}
                />
                <Route
                  path='/user-groups/users/:group_name'
                  element={<UserGroupUsers />}
                />
                {/* USERS ROUTING */}
                <Route path='/users' element={<UsersList />} />
                <Route
                  path='/users/:user_id'
                  element={<UserDetailPage />}
                />
                {/* CUSTOMERS ROUTING */}
                <Route path='/customers' element={<CustomersList />} />
                <Route
                  path='/customers/:customer_id'
                  element={<CustomerDetailPage />}
                />
                {/* SETTINGS ROUTING */}
                <Route path='/settings' element={<SettingsPage />} />
                <Route
                  exact
                  path='/settings/:setting_key'
                  element={<SettingDetailPage />}
                />
                <Route exact path='/assets' element={<AssetsList />} />
                <Route
                  exact
                  path='/asset/:asset_id'
                  element={<AssetDetail />}
                />
                <Route
                  exact
                  path='/statistics'
                  element={<StatisticsPage />}
                />
                <Route exact path='/coupons' element={<CouponsList />} />
                <Route
                  exact
                  path='/coupon/:coupon_id'
                  element={<CouponDetail />}
                />

                <Route exact path='/workflows' element={<WorkflowList />} />
                <Route
                  exact
                  path='/workflows/:workflow_id'
                  element={<WorkflowDetail />}
                />
                <Route exact path='/studio' element={<RedirectToPublicStudio />} />
                <Route
                  exact
                  path='/workflow-templates'
                  element={<WorkflowTemplatesAdmin />}
                />
                <Route
                  exact
                  path='/workflow-templates/:template_id'
                  element={<WorkflowTemplateDetail />}
                />
                <Route exact path='/events' element={<EventsList />} />
                <Route
                  exact
                  path='/events/:event_id'
                  element={<EventDetail />}
                />
                <Route exact path='/sessions' element={<SessionsList />} />
                <Route
                  exact
                  path='/sessions/:session_id'
                  element={<SessionDetail />}
                />
              </Routes>
            </Suspense>
          </div>
        </div>
      </div>
      <NotificationContainer />
    </Container>
  );
};

const App = () => {
  const getAuthInfo = async () => {
    const session = await fetchAuthSession();

    try {
      const { accessToken } = session.tokens;
      const { payload } = accessToken;
      const userGroups = payload['cognito:groups'];

      if (userGroups.includes('Admin')) {
        const { username } = payload;
        localStorage.setItem('@userName', username);
        localStorage.setItem('@accessToken', accessToken.toString());
      } else {
        signOut();
      }
    } catch (error) {
      localStorage.clear();
      signOut();
    }
  };

  useEffect(() => {
    getAuthInfo();
  }, []);

  return (
    <Authenticator>
      {() => (
        <Router>
          <AdminShell />
        </Router>
      )}
    </Authenticator>
  );
};

export default App;
